import { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Seo } from "@/seo/Seo";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Lock, Loader2, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { QUESTIONS } from "@/data/assessmentQuestions";
import { assessmentAccess, assessmentSave, assessmentComplete, assessmentQuestions } from "@/lib/api";
import { AIRO } from "@/constants/testIds";

const inputCls = "bg-[#0b0b0c] border-[#1f1f1f] text-white placeholder:text-zinc-600 focus-visible:ring-cyan-400/40 focus-visible:border-cyan-400/60 h-12";
const areaCls = "bg-[#0b0b0c] border-[#1f1f1f] text-white placeholder:text-zinc-600 focus-visible:ring-cyan-400/40 focus-visible:border-cyan-400/60 min-h-[140px] resize-y";

export default function Assessment() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const token = params.get("token") || "";

  const [phase, setPhase] = useState("gate"); // gate | preparing | flow | done | error
  const [email, setEmail] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [first, setFirst] = useState("");
  const [productName, setProductName] = useState("");
  const [responses, setResponses] = useState({});
  const [clar, setClar] = useState({});
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [questions, setQuestions] = useState([]);

  useEffect(() => { if (!token) { setPhase("error"); setErrorMsg("This assessment link is missing its secure token."); } }, [token]);

  const steps = questions;
  const current = steps[step];
  const total = steps.length;

  // Poll for the personalized questions while they generate in the background.
  const pollQuestions = async () => {
    for (let i = 0; i < 30; i++) {
      try {
        const d = await assessmentQuestions(token, email.trim());
        if (d.questions_status !== "generating" && (d.questions || []).length) {
          setQuestions(d.questions);
          setPhase("flow");
          return;
        }
      } catch (_) { /* keep polling */ }
      await new Promise((r) => setTimeout(r, 2500));
    }
    // Safety net: never strand the user — fall back to the base question set.
    setQuestions(QUESTIONS.map((q, i) => ({ id: `q${i + 1}`, number: i + 1, title: q.title, help: q.help, options: (q.options || []).slice(0, 4) })));
    setPhase("flow");
  };

  const verify = async () => {
    if (!/\S+@\S+\.\S+/.test(email)) return toast.error("Enter the email you used on your intake.");
    setVerifying(true);
    try {
      const data = await assessmentAccess(token, email.trim());
      setFirst(data.first_name || "");
      setProductName(data.product_name || "");
      setResponses(data.responses || {});
      setClar(data.clarifications || {});
      if (data.questions_status !== "generating" && (data.questions || []).length) {
        setQuestions(data.questions);
        setPhase("flow");
      } else {
        setPhase("preparing");
        pollQuestions();
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "We couldn't verify your access.");
    } finally { setVerifying(false); }
  };

  const setChoice = (val) => setResponses((r) => ({ ...r, [current.id]: { ...(r[current.id] || {}), value: val } }));
  const setOther = (val) => setResponses((r) => ({ ...r, [current.id]: { ...(r[current.id] || {}), value: "Other", other: val } }));

  const answered = () => {
    if (!current) return false;
    const r = responses[current.id] || {};
    if (r.value === "Other") return (r.other || "").trim().length > 1;
    return !!r.value;
  };

  const persist = async () => {
    try { await assessmentSave(token, email.trim(), responses, clar); }
    catch (_) { /* autosave best-effort */ }
  };

  const next = async () => {
    if (!answered()) return toast.error("Please answer this to continue.");
    setBusy(true);
    await persist();
    if (step < total - 1) { setStep((s) => s + 1); window.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" }); }
    else await finish();
    setBusy(false);
  };

  const finish = async () => {
    try {
      await assessmentComplete(token, email.trim(), responses, clar);
      setPhase("done");
      window.scrollTo({ top: 0, behavior: "auto" });
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not complete the assessment.");
    }
  };

  const back = () => { if (step > 0) { setStep((s) => s - 1); window.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" }); } };

  const trans = reduce ? {} : { initial: { opacity: 0, x: 24 }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0, x: -24 }, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] } };

  if (phase === "error")
    return <Shell><div className="max-w-lg text-center mx-auto pt-24"><h1 className="font-heading text-3xl">Link problem</h1><p className="mt-4 text-zinc-400">{errorMsg}</p><button onClick={() => navigate("/")} className="mt-8 mono-label border border-[#232323] px-6 py-3 rounded-full hover:border-cyan-400/50">Back home</button></div></Shell>;

  if (phase === "done") return <Done onHome={() => navigate("/")} />;

  return (
    <Shell>
      {/* NDA reassurance */}
      <div className="max-w-3xl mx-auto px-6 pt-6">
        <div className="flex items-center gap-2.5 text-xs text-zinc-500 border border-[#161616] rounded-full px-4 py-2.5 w-fit">
          <Lock className="h-3.5 w-3.5 text-cyan-400" />
          Your responses remain protected under the AIROTECH MVP Mutual NDA you already accepted.
        </div>
      </div>

      {phase === "gate" && (
        <div data-testid={AIRO.assessmentRoot} className="max-w-xl mx-auto px-6 pt-16 md:pt-24">
          <div className="mono-label text-cyan-400 mb-6">CONTINUE YOUR ASSESSMENT</div>
          <h1 className="font-heading font-extrabold tracking-tighter text-3xl md:text-5xl">Let's get more specific.</h1>
          <p className="mt-6 text-zinc-400 leading-relaxed">
            Your first answers gave us the big picture. These questions help us understand what it will actually
            take to build your product. To protect your idea, confirm the email you used on your intake.
          </p>
          <div className="mt-10 space-y-3">
            <Label className="mono-label text-zinc-400">Your email</Label>
            <Input data-testid={AIRO.assessmentEmail} className={inputCls} type="email" value={email}
              onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com"
              onKeyDown={(e) => e.key === "Enter" && verify()} />
          </div>
          <button data-testid={AIRO.assessmentVerify} onClick={verify} disabled={verifying}
            className="mt-6 inline-flex items-center gap-2 bg-white text-black font-medium px-7 py-4 rounded-full mono-label hover:bg-cyan-400 transition-colors duration-200 disabled:opacity-50">
            {verifying ? <><Loader2 className="h-4 w-4 animate-spin" /> Verifying…</> : <>Continue <ArrowRight className="h-4 w-4" /></>}
          </button>
        </div>
      )}

      {phase === "preparing" && (
        <div data-testid={AIRO.assessmentPreparing} className="max-w-xl mx-auto px-6 pt-24 md:pt-32 text-center">
          <span className="inline-flex h-14 w-14 rounded-full bg-cyan-400/10 border border-cyan-400/25 items-center justify-center mb-8">
            <Loader2 className="h-6 w-6 text-cyan-400 animate-spin" />
          </span>
          <h1 className="font-heading font-extrabold tracking-tighter text-3xl md:text-4xl">Preparing your personalized assessment…</h1>
          <p className="mt-6 text-zinc-400 leading-relaxed">
            AIROTECH is tailoring ten discovery questions to {productName ? <span className="text-cyan-300">{productName}</span> : "your product"}. This takes just a moment.
          </p>
        </div>
      )}

      {phase === "flow" && current && (
        <div data-testid={AIRO.assessmentRoot} className="max-w-3xl mx-auto px-6 pt-10 md:pt-14 pb-24">
          <div className="h-0.5 bg-[#141414] rounded-full mb-10">
            <div className="h-full bg-primary rounded-full transition-[width] duration-500" style={{ width: `${((step + 1) / total) * 100}%` }} />
          </div>
          <AnimatePresence mode="wait">
            <motion.div key={current.id} {...trans}>
              <div data-testid={AIRO.assessmentProgress} className="mono-label text-cyan-400 mb-8">
                {String(step + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
              </div>
              <h1 className="font-heading font-extrabold tracking-tighter text-3xl md:text-5xl leading-[1.02]">{current.title}</h1>
              {current.help && <p className="mt-6 text-base md:text-lg text-zinc-400 leading-relaxed max-w-2xl">{current.help}</p>}

              {/* four answer cards + Other */}
              <div className="mt-8 grid sm:grid-cols-2 gap-3">
                {[...(current.options || []), "Other"].map((opt, i) => {
                  const sel = (responses[current.id] || {}).value === opt;
                  return (
                    <button key={opt + i} data-testid={`${AIRO.assessmentOption}-${i}`} onClick={() => setChoice(opt)}
                      aria-pressed={sel}
                      className={`text-left rounded-xl border p-4 transition-colors duration-200 ${sel ? "border-cyan-400/60 bg-cyan-400/[0.06] text-cyan-100" : "border-[#1f1f1f] bg-[#0a0a0a] text-zinc-300 hover:border-[#2a2a2a]"}`}>
                      {opt}
                    </button>
                  );
                })}
              </div>

              {/* Other → inline text, selection preserved */}
              <AnimatePresence>
                {(responses[current.id] || {}).value === "Other" && (
                  <motion.div
                    initial={reduce ? {} : { opacity: 0, height: 0 }}
                    animate={reduce ? {} : { opacity: 1, height: "auto" }}
                    exit={reduce ? {} : { opacity: 0, height: 0 }}
                    className="overflow-hidden">
                    <Label className="mono-label text-zinc-400 mt-6 block">Tell us what you have in mind.</Label>
                    <Textarea data-testid={AIRO.assessmentOther} className={`${areaCls} mt-2`}
                      value={(responses[current.id] || {}).other || ""} onChange={(e) => setOther(e.target.value)}
                      placeholder="Describe it in your own words…" />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </AnimatePresence>

          <div className="mt-12 flex items-center justify-between">
            <button data-testid={AIRO.assessmentBack} onClick={back} disabled={step === 0}
              className="inline-flex items-center gap-2 mono-label text-zinc-500 hover:text-white transition-colors duration-200 disabled:opacity-30">
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            <button data-testid={AIRO.assessmentNext} onClick={next} disabled={busy}
              className="group inline-flex items-center gap-2 bg-white text-black font-medium px-7 py-3.5 rounded-full mono-label hover:bg-cyan-400 transition-colors duration-200 disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{step === total - 1 ? "Complete" : "Continue"} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-200" /></>}
            </button>
          </div>
        </div>
      )}
    </Shell>
  );
}

const Shell = ({ children }) => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Seo noindex path="/assessment" title="Product Assessment · AIROTECH" />
      <header className="border-b border-[#141414] sticky top-0 z-40 bg-black/80 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto px-6 h-[72px] flex items-center">
          <button onClick={() => navigate("/")} aria-label="AIROTECH home">
            <img src="/airotech-mark.png" alt="AIROTECH MVP" className="h-8 w-auto" />
          </button>
        </div>
      </header>
      {children}
    </div>
  );
};

const Done = ({ onHome }) => (
  <Shell>
    <div data-testid={AIRO.assessmentComplete} className="max-w-xl mx-auto px-6 text-center pt-24 md:pt-32 relative">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
        <span className="inline-flex h-14 w-14 rounded-full bg-cyan-400/10 border border-cyan-400/25 items-center justify-center mb-8">
          <CheckCircle2 className="h-6 w-6 text-cyan-400" />
        </span>
        <h1 className="font-heading font-extrabold tracking-tighter text-5xl md:text-6xl text-glow">ASSESSMENT COMPLETE.</h1>
        <p className="mt-6 text-lg text-zinc-300 leading-relaxed">
          Thank you. We've got what we need to take a much closer look at your product. Our next step is to analyze
          what you've shared and prepare your AIROTECH Product Review.
        </p>
        <p className="mt-6 mono-label text-cyan-400">YOUR IDEA → ASSESSMENT → AIROTECH ANALYSIS → PRODUCT REVIEW</p>
        <p className="mt-6 text-sm text-zinc-500">You'll receive an email when we're ready for the next step.</p>
        <button onClick={onHome} className="mt-10 mono-label border border-[#232323] px-7 py-3.5 rounded-full hover:border-cyan-400/50 hover:text-cyan-300 transition-colors duration-200">
          Back to Home
        </button>
      </motion.div>
    </div>
  </Shell>
);
